import type { Activity } from "./dataStore";
import type { ActivityRegistrationOptionsConfig } from "../../../utils/activityRegistrationOptions";
import {
  emptyRegistrationOptionQuestion,
  validateRegistrationOptionsForm,
} from "../../../utils/activityRegistrationOptions";
import {
  type ActivityCancelDeadlineMode,
  normalizeCancelDeadlineMode,
} from "../../../utils/activityCancelRegistration";
import { mergeLotteryIntoActivitySummaryJson } from "../../../utils/activitySummaryLottery";
import { splitActivityInstantChina } from "../../../utils/activityCheckInDate";
import {
  ALL_ACTIVITY_GROUPS,
  ALL_ACTIVITY_LEVELS,
  normalizeActivityTargetAudience,
  parseActivityTargetAudience,
  type ActivityTargetAudience,
} from "../../../utils/activityTargetAudience";
import { validateParticipationPoints, type ParticipationPointsBounds } from "../../../api/pointsRules";

export type ActivityFormStep = 1 | 2 | 3;

export const ACTIVITY_FORM_STEPS: { step: ActivityFormStep; label: string }[] = [
  { step: 1, label: "活动信息" },
  { step: 2, label: "时间地点" },
  { step: 3, label: "活动规则" },
];

export type ActivityFormData = {
  title: string;
  description: string;
  activityType: string;
  dateDay: string;
  dateTime: string;
  endDateDay: string;
  endDateTime: string;
  regStartDay: string;
  regStartTime: string;
  regEndDay: string;
  regEndTime: string;
  location: string;
  checkInFenceLat: string;
  checkInFenceLng: string;
  checkInFenceRadiusMeters: number;
  checkInEarlyMinutes: number;
  checkInLateLimitEnabled: boolean;
  checkInLateMinutes: number;
  requireCheckOut: boolean;
  checkOutAllowEarly: boolean;
  checkOutEarlyMinutes: number;
  capacity: number;
  expenseType: string;
  onlinePayAmountYuanText: string;
  onlinePayDepositYuanText: string;
  allowCancelWithRefund: boolean;
  cancelDeadlineMode: ActivityCancelDeadlineMode;
  cancelDeadlineDay: string;
  cancelDeadlineTime: string;
  registrationOptions: ActivityRegistrationOptionsConfig;
  allowMemberFriendGuestRegistration: boolean;
  memberFriendGuestRegistrationQuota: number;
  showEnrollmentCountToMembers: boolean;
  showShareOnRegistrationPage: boolean;
  targetAudience: ActivityTargetAudience;
  /** 关联协会：tenantId + 单独名额 */
  linkedAssociations: Array<{ tenantId: string; tenantName: string; quota: number }>;
  participationPoints: number;
  enableParticipationPointDeduction: boolean;
  notRegisteredDeductionPoints: number;
  notCheckedInDeductionPoints: number;
  tags: string;
  ageMin: number;
  ageMax: number;
  coverUrl: string;
  lotteryEnabled: boolean;
  requireDocSign: boolean;
  requiredDocIds: string[];
  locationMapPicked: boolean;
};

export const emptyActivityForm = (participationDefault = 30): ActivityFormData => ({
  title: "",
  description: "",
  activityType: "志愿活动",
  dateDay: "",
  dateTime: "",
  endDateDay: "",
  endDateTime: "",
  regStartDay: "",
  regStartTime: "",
  regEndDay: "",
  regEndTime: "",
  location: "",
  checkInFenceLat: "",
  checkInFenceLng: "",
  checkInFenceRadiusMeters: 0,
  checkInEarlyMinutes: 30,
  checkInLateLimitEnabled: false,
  checkInLateMinutes: 120,
  requireCheckOut: false,
  checkOutAllowEarly: false,
  checkOutEarlyMinutes: 0,
  capacity: 0,
  expenseType: "免费",
  onlinePayAmountYuanText: "",
  onlinePayDepositYuanText: "",
  allowCancelWithRefund: false,
  cancelDeadlineMode: "reg_end",
  cancelDeadlineDay: "",
  cancelDeadlineTime: "",
  registrationOptions: { enabled: false, items: [] },
  allowMemberFriendGuestRegistration: false,
  memberFriendGuestRegistrationQuota: 2,
  showEnrollmentCountToMembers: true,
  showShareOnRegistrationPage: true,
  targetAudience: { levels: [ALL_ACTIVITY_LEVELS], groups: [ALL_ACTIVITY_GROUPS] },
  linkedAssociations: [],
  participationPoints: participationDefault,
  enableParticipationPointDeduction: false,
  notRegisteredDeductionPoints: 0,
  notCheckedInDeductionPoints: 0,
  tags: "",
  ageMin: 0,
  ageMax: 0,
  coverUrl: "",
  lotteryEnabled: false,
  requireDocSign: false,
  requiredDocIds: [],
  locationMapPicked: false,
});

export function activityFormLocationDisplay(location: string): string {
  return String(location || "").trim();
}

function resolveDateTime(day: string, time: string, fallback: string): string {
  const d = day.trim();
  if (!d) return "";
  const ti = time.trim();
  return ti ? `${d} ${ti}` : `${d} ${fallback}`;
}

export function activityFormScheduleComplete(form: ActivityFormData): boolean {
  return Boolean(
    form.dateDay.trim() &&
      form.dateTime.trim() &&
      form.endDateDay.trim() &&
      form.endDateTime.trim() &&
      form.regStartDay.trim() &&
      form.regStartTime.trim() &&
      form.regEndDay.trim() &&
      form.regEndTime.trim(),
  );
}

export function activityFormStep2Complete(form: ActivityFormData): boolean {
  return (
    activityFormScheduleComplete(form) &&
    Boolean(activityFormLocationDisplay(form.location).trim()) &&
    Math.floor(Number(form.capacity) || 0) >= 1
  );
}

export function validateActivityFormStep1(form: ActivityFormData): string | null {
  if (!form.title.trim()) return "请填写活动名称";
  return null;
}

export function validateActivityFormStep2(form: ActivityFormData): string | null {
  if (!form.dateDay.trim() || !form.dateTime.trim()) return "请填写活动开始日期与时间";
  if (!form.endDateDay.trim() || !form.endDateTime.trim()) return "请填写活动结束日期与时间";
  if (!form.regStartDay.trim() || !form.regStartTime.trim()) return "请填写报名开始日期与时间";
  if (!form.regEndDay.trim() || !form.regEndTime.trim()) return "请填写报名截止日期与时间";
  if (!activityFormLocationDisplay(form.location).trim()) return "请通过地图选择活动地点";
  if (Math.floor(Number(form.capacity) || 0) < 1) return "请填写人数上限";
  return null;
}

export function activityFormFromActivity(a: Activity): ActivityFormData {
  const { day: dateDay, time: dateTime } = splitActivityInstantChina(a.date);
  const { day: endDateDay, time: endDateTime } = splitActivityInstantChina(a.eventEndDate);
  const { day: regStartDay, time: regStartTimeRaw } = splitActivityInstantChina(a.regStartDate);
  const { day: regEndDay, time: regEndTimeRaw } = splitActivityInstantChina(a.regEndDate);
  const { day: cancelDeadlineDay, time: cancelDeadlineTimeRaw } = splitActivityInstantChina(a.cancelDeadlineAt);
  return {
    title: a.title || "",
    description: (a.description || "").toString(),
    activityType: (a.activityType || "志愿活动").toString(),
    dateDay,
    dateTime: dateTime || "09:00",
    endDateDay,
    endDateTime: endDateTime || "18:00",
    regStartDay,
    regStartTime: regStartTimeRaw || "09:00",
    regEndDay,
    regEndTime: regEndTimeRaw || "18:00",
    location: activityFormLocationDisplay(a.location),
    checkInFenceLat: a.checkInFenceLat != null ? String(a.checkInFenceLat) : "",
    checkInFenceLng: a.checkInFenceLng != null ? String(a.checkInFenceLng) : "",
    checkInFenceRadiusMeters: Number(a.checkInFenceRadiusMeters ?? 0) || 0,
    checkInEarlyMinutes: Math.max(0, Number(a.checkInEarlyMinutes ?? 0) || 0),
    checkInLateLimitEnabled: a.checkInLateMinutes != null && Number(a.checkInLateMinutes) >= 0,
    checkInLateMinutes: Math.max(0, Number(a.checkInLateMinutes ?? 120) || 0),
    requireCheckOut: Boolean(a.requireCheckOut),
    checkOutAllowEarly: Boolean(a.checkOutAllowEarly),
    checkOutEarlyMinutes: Math.max(0, Number(a.checkOutEarlyMinutes ?? 0) || 0),
    capacity: a.capacity && a.capacity >= 1 ? a.capacity : 0,
    expenseType: (a.expenseType || "免费").toString(),
    onlinePayAmountYuanText: (() => {
      const fen = Number(a.onlinePayAmountFen ?? 0) || 0;
      if (fen <= 0) return "";
      const yuan = (fen / 100).toFixed(2);
      return yuan.replace(/\.?0+$/, "");
    })(),
    onlinePayDepositYuanText: (() => {
      const fen = Number(a.onlinePayDepositAmountFen ?? 0) || 0;
      if (fen <= 0) return "";
      const yuan = (fen / 100).toFixed(2);
      return yuan.replace(/\.?0+$/, "");
    })(),
    allowCancelWithRefund: Boolean(a.allowCancelWithRefund),
    cancelDeadlineMode: normalizeCancelDeadlineMode(a.cancelDeadlineMode ?? "event_start"),
    cancelDeadlineDay,
    cancelDeadlineTime: cancelDeadlineTimeRaw || "18:00",
    registrationOptions: a.registrationOptions ?? { enabled: false, items: [] },
    allowMemberFriendGuestRegistration: Boolean(a.allowMemberFriendGuestRegistration),
    memberFriendGuestRegistrationQuota: Math.max(0, Number(a.memberFriendGuestRegistrationQuota ?? 0) || 0),
    showEnrollmentCountToMembers: a.showEnrollmentCountToMembers !== false,
    showShareOnRegistrationPage: a.showShareOnRegistrationPage !== false,
    targetAudience: normalizeActivityTargetAudience(parseActivityTargetAudience(a.targetAudience)),
    linkedAssociations: Array.isArray(a.linkedAssociations)
      ? a.linkedAssociations
          .filter((x) => x?.tenantId)
          .map((x) => ({
            tenantId: String(x.tenantId),
            tenantName: String(x.tenantName || x.shortName || x.tenantId),
            quota: Math.max(0, Number(x.quota) || 0),
          }))
      : [],
    participationPoints: Number(a.participationPoints ?? 0) || 0,
    enableParticipationPointDeduction: Boolean(a.enableParticipationPointDeduction),
    notRegisteredDeductionPoints: Math.max(0, Number(a.notRegisteredDeductionPoints ?? 0) || 0),
    notCheckedInDeductionPoints: Math.max(0, Number(a.notCheckedInDeductionPoints ?? 0) || 0),
    tags: (a.tags || "").toString(),
    ageMin: Number(a.ageMin ?? 0) || 0,
    ageMax: Number(a.ageMax ?? 0) || 0,
    coverUrl: (a.coverUrl || "").toString(),
    lotteryEnabled: Boolean(a.lotteryEnabled),
    requireDocSign: Boolean(a.requireDocSign),
    requiredDocIds: Array.isArray(a.requiredDocIds) ? [...a.requiredDocIds] : [],
    locationMapPicked: Boolean(activityFormLocationDisplay(a.location)),
  };
}

export function draftActivityFromForm(
  form: ActivityFormData,
  edit: Activity | null,
  allowedDocIds: string[],
): Activity {
  const onlineFen =
    form.expenseType.trim() === "在线支付"
      ? Math.round(Math.max(0, Number(form.onlinePayAmountYuanText || "0")) * 100)
      : 0;
  const onlineDepositFen =
    form.expenseType.trim() === "在线支付"
      ? Math.round(Math.max(0, Number(form.onlinePayDepositYuanText || "0")) * 100)
      : 0;
  const selectedDocIds = form.requiredDocIds.filter((id) => allowedDocIds.includes(id));
  return {
    id: edit?.id ?? "",
    title: form.title.trim(),
    activityType: form.activityType.trim() || "志愿活动",
    date: resolveDateTime(form.dateDay, form.dateTime.trim(), "09:00"),
    eventEndDate: resolveDateTime(form.endDateDay, form.endDateTime.trim(), "18:00"),
    location: form.location.trim(),
    capacity: Math.max(1, Number(form.capacity) || 1),
    enrolled: edit?.enrolled ?? 0,
    status: edit?.status ?? "draft",
    regStartDate: resolveDateTime(form.regStartDay, form.regStartTime.trim(), "09:00"),
    regEndDate: resolveDateTime(form.regEndDay, form.regEndTime.trim(), "18:00"),
    expenseType: form.expenseType.trim(),
    onlinePayAmountFen: onlineFen,
    onlinePayDepositAmountFen: onlineDepositFen,
    allowCancelWithRefund: Boolean(form.allowCancelWithRefund),
    cancelDeadlineMode: form.cancelDeadlineMode,
    cancelDeadlineAt:
      form.cancelDeadlineMode === "custom"
        ? resolveDateTime(form.cancelDeadlineDay, form.cancelDeadlineTime.trim(), "18:00")
        : undefined,
    registrationOptions: form.registrationOptions,
    allowMemberFriendGuestRegistration: Boolean(form.allowMemberFriendGuestRegistration),
    memberFriendGuestRegistrationQuota: form.allowMemberFriendGuestRegistration
      ? Math.max(1, Math.min(99999, Math.floor(Number(form.memberFriendGuestRegistrationQuota) || 0)))
      : 0,
    showEnrollmentCountToMembers: Boolean(form.showEnrollmentCountToMembers),
    showShareOnRegistrationPage: Boolean(form.showShareOnRegistrationPage),
    targetAudience: normalizeActivityTargetAudience(form.targetAudience),
    linkedAssociations: (form.linkedAssociations || [])
      .filter((x) => x.tenantId)
      .map((x) => ({
        tenantId: x.tenantId,
        tenantName: x.tenantName,
        quota: Math.max(0, Math.floor(Number(x.quota) || 0)),
      })),
    description: form.description.trim() || undefined,
    tags: form.tags.trim() || undefined,
    participationPoints: Number(form.participationPoints) || 0,
    enableParticipationPointDeduction: Boolean(form.enableParticipationPointDeduction),
    notRegisteredDeductionPoints: Math.max(0, Math.floor(Number(form.notRegisteredDeductionPoints) || 0)),
    notCheckedInDeductionPoints: Math.max(0, Math.floor(Number(form.notCheckedInDeductionPoints) || 0)),
    coverUrl: form.coverUrl.trim() || undefined,
    requireDocSign: form.requireDocSign,
    requiredDocIds: form.requireDocSign ? selectedDocIds : undefined,
    ageMin: Number(form.ageMin) || 0,
    ageMax: Number(form.ageMax) || 0,
    checkInFenceLat: form.checkInFenceLat.trim() ? Number(form.checkInFenceLat.trim()) : undefined,
    checkInFenceLng: form.checkInFenceLng.trim() ? Number(form.checkInFenceLng.trim()) : undefined,
    checkInFenceRadiusMeters: form.checkInFenceRadiusMeters > 0 ? form.checkInFenceRadiusMeters : undefined,
    checkInEarlyMinutes: Math.max(0, Number(form.checkInEarlyMinutes) || 0),
    checkInLateMinutes: form.checkInLateLimitEnabled
      ? Math.max(0, Math.min(7 * 24 * 60, Number(form.checkInLateMinutes) || 0))
      : null,
    requireCheckOut: form.requireCheckOut,
    checkOutAllowEarly: form.requireCheckOut ? form.checkOutAllowEarly : false,
    checkOutEarlyMinutes:
      form.requireCheckOut && form.checkOutAllowEarly
        ? Math.max(0, Math.min(7 * 24 * 60, Number(form.checkOutEarlyMinutes) || 0))
        : 0,
    rejectionNote: edit?.rejectionNote,
    approvedAt: edit?.approvedAt,
    activitySummaryJson: mergeLotteryIntoActivitySummaryJson(
      edit?.activitySummaryJson ?? null,
      form.lotteryEnabled,
    ),
    lotteryEnabled: form.lotteryEnabled,
  };
}

export function validateActivityFormSubmit(
  form: ActivityFormData,
  allowedDocIds: string[],
  participationBounds: ParticipationPointsBounds,
): string | null {
  const step1 = validateActivityFormStep1(form);
  if (step1) return step1;
  const step2 = validateActivityFormStep2(form);
  if (step2) return step2;
  const onlineFen =
    form.expenseType.trim() === "在线支付"
      ? Math.round(Math.max(0, Number(form.onlinePayAmountYuanText || "0")) * 100)
      : 0;
  if (form.expenseType.trim() === "在线支付" && onlineFen < 1) {
    return "请填写在线支付的基础费用（元）";
  }
  if (form.allowMemberFriendGuestRegistration) {
    const q = Math.floor(Number(form.memberFriendGuestRegistrationQuota) || 0);
    if (q < 1 || q > 99999) return "请填写 1～99999 的每位会员可登记人数";
  }
  const regOptErr = validateRegistrationOptionsForm(form.registrationOptions);
  if (regOptErr) return regOptErr;
  if (form.allowCancelWithRefund && form.cancelDeadlineMode === "custom") {
    if (!form.cancelDeadlineDay.trim() || !form.cancelDeadlineTime.trim()) {
      return "请填写自定义取消报名截止时间";
    }
  }
  const selectedDocIds = form.requiredDocIds.filter((id) => allowedDocIds.includes(id));
  if (form.requireDocSign && selectedDocIds.length === 0) {
    return "请至少选择一份免责声明";
  }
  return validateParticipationPoints(form.participationPoints, participationBounds);
}

export function activityListStatusPill(status: string): { label: string; className: string } {
  const s = String(status || "").toLowerCase();
  if (s === "draft") return { label: "草稿", className: "bg-gray-100 text-gray-600" };
  if (s === "ongoing") return { label: "报名中", className: "bg-green-50 text-green-600" };
  if (s === "full") return { label: "已满员", className: "bg-red-50 text-red-600" };
  if (s === "ended") return { label: "已结束", className: "bg-gray-100 text-gray-500" };
  if (s === "pending") return { label: "待审核", className: "bg-amber-50 text-amber-600" };
  return { label: status || "未知", className: "bg-gray-100 text-gray-600" };
}

export { emptyRegistrationOptionQuestion };
